import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { InventoryPolicyControlScope } from '../model/controlCenter.types';

const DEFAULT_SCOPE: InventoryPolicyControlScope = 'central';

export function useInventoryPolicyControlScope() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.role?.id === 1;
  const rawScope = searchParams.get('scope');
  const scope: InventoryPolicyControlScope = rawScope === 'stores' ? 'stores' : DEFAULT_SCOPE;

  const setScope = useCallback(
    (nextScope: InventoryPolicyControlScope) => {
      if (!isAdmin) return;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('scope', nextScope);
      setSearchParams(nextParams, { replace: true });
    },
    [isAdmin, searchParams, setSearchParams]
  );

  const buildScopedPath = useCallback(
    (pathname: string) => {
      const nextParams = new URLSearchParams();
      nextParams.set('scope', scope);
      return `${pathname}?${nextParams.toString()}`;
    },
    [scope]
  );

  return useMemo(
    () => ({
      scope,
      setScope,
      showScopeSelector: isAdmin,
      buildScopedPath,
    }),
    [scope, setScope, isAdmin, buildScopedPath]
  );
}
